import BlocksAvatar from "@/src/components/ui-kit/blocks-avatar";
import DropDown from "@/src/components/ui-kit/drop-down";
import UserAvatar from "@/src/components/ui-kit/user-avatar";
import { usePermissions } from "@/src/hooks/use-permissions";
import { useTheme } from "@/src/styles/theme-provider";
import { stripHtml } from "@/src/utils/html.utils";
import { LinearGradient } from "expo-linear-gradient";
import { t } from "i18next";
import { useState } from "react";
import {
  Image,
  ImageSourcePropType,
  Platform,
  Pressable,
  ScrollView,
  Text,
  TouchableOpacity,
  View,
} from "react-native";

type NewsCardBaseProps = Readonly<{
  title: string;
  date: string;
  tags: string[];
  imageUrls?: ImageSourcePropType;
  content: string;
  onPress?: () => void;
  onSave?: () => void;
  onShare?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onPin: () => void;
  authorName?: string;
  authorRole?: string;
  authorId?: string;
  authorImage?: ImageSourcePropType;
  isSaved?: boolean;
  isPinned?: boolean;
  isOwner?: boolean;
}>;

type NewsCardProps = NewsCardBaseProps & {
  variant: "small" | "large";
};

export default function NewsCard({
  title,
  date,
  tags,
  imageUrls,
  content,
  onPress,
  onSave,
  onShare,
  onEdit,
  onDelete,
  onPin,
  variant,
  authorName,
  authorRole,
  authorId,
  authorImage,
  isSaved,
  isPinned,
  isOwner = false,
}: NewsCardProps) {
  const { themeVars } = useTheme();
  const { canUpdate, canDelete } = usePermissions();
  const isWeb = Platform.OS === "web";
  const [isCardHovered, setIsCardHovered] = useState(false);
  const [isCardPressed, setIsCardPressed] = useState(false);
  const isHighlighted = isWeb ? isCardHovered : isCardPressed;
  const gradientColors: [string, string] = [
    "transparent",
    themeVars["--color-chalk-default"] as string,
  ];

  const plainContent = stripHtml(content);

  const dropdownItems = [
    {
      label: isSaved ? t("BTN_UNSAVE") : t("BTN_SAVE"),
      onPress: () => onSave?.(),
    },
    { label: t("LABEL_SHARE"), onPress: () => onShare?.() },
    ...(canUpdate("news")
      ? [
          {
            label: isPinned ? t("BTN_UNPIN") : t("BTN_PIN"),
            onPress: () => onPin?.(),
          },
        ]
      : []),
    ...(isOwner && canUpdate("news")
      ? [{ label: t("BTN_UPDATE"), onPress: () => onEdit?.() }]
      : []),
    ...(isOwner && canDelete("news")
      ? [
          {
            label: t("BTN_DELETE"),
            onPress: () => onDelete?.(),
            destructive: true,
          },
        ]
      : []),
  ];

  const [firstName, ...rest] = (authorName ?? "").split(" ");
  const lastName = rest.join(" ");

  const MenuButton = () => (
    <TouchableOpacity
      activeOpacity={0.7}
      onPress={(event) => event.stopPropagation()}
    >
      <DropDown items={dropdownItems} />
    </TouchableOpacity>
  );

  const TagsRow = () => (
    <View className="flex-row items-center gap-2 flex-wrap">
      <Text className="text-xs text-ash-1">{date}</Text>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={{ gap: 8 }}
      >
        {tags.map((tag, index) => (
          <Text key={index} className="text-xs text-primary font-semibold">
            {tag}
          </Text>
        ))}
      </ScrollView>
    </View>
  );

  if (variant === "large") {
    return (
      <Pressable
        onPress={onPress}
        className="flex-1 rounded-xl p-3 gap-3 bg-chalk active:opacity-90 md:border"
        onHoverIn={() => setIsCardHovered(true)}
        onHoverOut={() => setIsCardHovered(false)}
        onPressIn={() => !isWeb && setIsCardPressed(true)}
        onPressOut={() => !isWeb && setIsCardPressed(false)}
        style={{
          borderColor: isHighlighted
            ? (themeVars["--color-primary-default"] as string)
            : (themeVars["--color-ash-3"] as string),
        }}
      >
        <View className="flex-row justify-between items-center">
          {authorName && (
            <View className="flex-row gap-2 items-center flex-1">
              {authorId ? (
                <BlocksAvatar
                  userId={authorId}
                  size={36}
                  className="rounded-full h-9 w-9"
                />
              ) : authorImage ? (
                <Image
                  source={authorImage}
                  className="rounded-full h-9 w-9"
                  resizeMode="contain"
                />
              ) : (
                <UserAvatar
                  firstName={firstName}
                  lastName={lastName}
                  size={36}
                />
              )}
              <View className="flex-1 min-w-0">
                <Text
                  className="text-sm font-semibold text-onyx"
                  numberOfLines={1}
                >
                  {authorName}
                </Text>
                {!!authorRole && (
                  <Text className="text-xs text-ash-1" numberOfLines={1}>
                    {authorRole}
                  </Text>
                )}
              </View>
            </View>
          )}
          <MenuButton />
        </View>

        <Text
          className="text-sm font-bold text-onyx"
          numberOfLines={2}
          style={{ lineHeight: 20, minHeight: 40 }}
        >
          {title}
        </Text>

        <TagsRow />

        <View style={{ minHeight: 60 }}>
          <Text
            className="text-slate text-sm leading-5"
            numberOfLines={3}
            style={{ lineHeight: 20 }}
          >
            {plainContent}
          </Text>
          <LinearGradient
            colors={gradientColors}
            className="absolute bottom-0 left-0 right-0 h-6"
          />
        </View>

        {imageUrls && (
          <Image
            source={imageUrls}
            className="w-full h-[52%] rounded-xl"
            resizeMode="cover"
          />
        )}
      </Pressable>
    );
  }

  return (
    <Pressable
      onPress={onPress}
      className="p-3 rounded-xl flex-row gap-3 bg-chalk active:opacity-90 md:border"
      onHoverIn={() => setIsCardHovered(true)}
      onHoverOut={() => setIsCardHovered(false)}
      onPressIn={() => !isWeb && setIsCardPressed(true)}
      onPressOut={() => !isWeb && setIsCardPressed(false)}
      style={{
        borderColor: isHighlighted
          ? (themeVars["--color-primary-default"] as string)
          : (themeVars["--color-ash-3"] as string),
      }}
    >
      {imageUrls && (
        <Image
          source={imageUrls}
          className="rounded-xl"
          style={{ width: "40%", aspectRatio: 80 / 40 }}
          resizeMode="cover"
        />
      )}

      <View className="flex-1">
        <View className="flex-row justify-between items-start mb-2">
          <Text
            className="text-onyx text-sm font-semibold flex-1 pr-2"
            numberOfLines={2}
          >
            {title}
          </Text>
          <MenuButton />
        </View>
        <TagsRow />
      </View>
    </Pressable>
  );
}
